import { useCallback, useState } from 'react';
import { trackEvent, trackPageView, trackUserAction } from '../helpers/firebaseClient';
import { useUser } from '../context/UserContext';
import { LOCAL_STORAGE_NAMES } from '../constants/constants';

export const useAnalytics = () => {
  const { user: userDetails } = useUser();

  const getPermission = () => {
    const permissionsRaw = localStorage.getItem(LOCAL_STORAGE_NAMES.appAnalyticsPermission);
    const permissions = permissionsRaw ? JSON.parse(permissionsRaw) : {};
    return Boolean(
      userDetails?.id &&
      (permissions[userDetails.id] === true || permissions[userDetails.id] === 'true'),
    );
  };
  const [hasAnalyticsPermission, setHasAnalyticsPermission] = useState(getPermission());

  const trackPage = useCallback(
    (pageName: string) => {
      if (!getPermission()) return;
      trackPageView(pageName);
    },
    [userDetails, hasAnalyticsPermission],
  );

  const trackAction = useCallback(
    (
      action: string,
      category?: string,
      label?: string,
      value?: number,
      additionalData?: Record<string, string>,
    ) => {
      if (!getPermission()) return;
      trackUserAction(action, category, label, value);
      if (additionalData) {
        trackEvent(`${category}_${action}`, {
          ...additionalData,
        });
      }
    },
    [userDetails],
  );

  const trackBusinessEvent = useCallback(
    (eventName: string) => {
      if (!getPermission()) return;
      trackEvent(eventName);
    },
    [userDetails, hasAnalyticsPermission],
  );

  const setAnalyticsPermission = (enabled: boolean) => {
    const userId = userDetails?.id;
    if (!userId) return;
    const permissionsRaw = localStorage.getItem(LOCAL_STORAGE_NAMES.appAnalyticsPermission);
    const permissions = permissionsRaw ? JSON.parse(permissionsRaw) : {};
    permissions[userId] = enabled;
    localStorage.setItem(LOCAL_STORAGE_NAMES.appAnalyticsPermission, JSON.stringify(permissions));
    setHasAnalyticsPermission(enabled);
  };

  return {
    trackPage,
    trackAction,
    trackBusinessEvent,
    setAnalyticsPermission,
    hasAnalyticsPermission,
  };
};
